/**
 * Auth hooks — login, register, logout, current user
 */

import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api.js';

interface User {
  id: string;
  email: string;
  name: string;
  auth_provider: string;
  status: string;
  email_verified: boolean;
  created_at: string;
}

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  role: string;
  tier: string;
}

interface AuthState {
  user: User | null;
  tenants: TenantInfo[];
  currentTenantId: string | null;
  currentRole: string | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null, tenants: [], currentTenantId: null, currentRole: null, loading: true, error: null,
  });

  const fetchMe = useCallback(async () => {
    try {
      const data = await api.get<{
        user: User | null;
        tenants: TenantInfo[];
        current_tenant_id: string | null;
        current_role: string | null;
      }>('/auth/me');
      setState({
        user: data.user, tenants: data.tenants ?? [],
        currentTenantId: data.current_tenant_id, currentRole: data.current_role,
        loading: false, error: null,
      });
    } catch {
      setState(prev => ({ ...prev, user: null, loading: false }));
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await api.post('/auth/login', { email, password });
      await fetchMe();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      setState(prev => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  }, [fetchMe]);

  const register = useCallback(async (data: {
    email: string; password: string; name: string; company_name: string; tier?: string;
  }) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await api.post('/auth/register', data);
      await fetchMe();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Registration failed';
      setState(prev => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  }, [fetchMe]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setState({ user: null, tenants: [], currentTenantId: null, currentRole: null, loading: false, error: null });
    const hubUrl = import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com';
    window.location.href = `${hubUrl}/login`;
  }, []);

  return { ...state, login, register, logout, refresh: fetchMe };
}
